/**
 * What you have sat with, and when.
 *
 * This lives on the device so the selector can avoid repeating a story heard last
 * week. It is deliberately not a streak counter, a total-minutes badge or a calendar
 * — turning a practice into a scoreboard is its own kind of harm. An optional account
 * can union the same rows onto another device; without one, nothing here leaves the
 * phone. That union is not a copy kept elsewhere for you.
 */

import { signal, effect } from '@preact/signals';
import type { PlayRecord } from '../session/select';

export const HISTORY_KEY = 'brahm.history.v1';
export const HISTORY_CLEAR_KEY = 'brahm.history.clear.v1';

/** Keep two years. Long enough for the story penalty to be meaningful, then forget. */
export const HISTORY_RETAIN_MS = 2 * 365 * 86_400_000;

/** Server `parseHistoryEvents` rejects anything outside this. */
export const EVENT_ID_RE = /^[A-Za-z0-9_-]{16,96}$/;

export type HistoryRecord = PlayRecord & { eventId: string };

function load(): HistoryRecord[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const all = JSON.parse(raw) as Array<PlayRecord & { eventId?: string }>;
    const cutoff = Date.now() - HISTORY_RETAIN_MS;
    return all.filter((row) => row.at > cutoff).sort((a, b) => a.at - b.at)
      .map((row) => ({
        recordingId: row.recordingId,
        at: row.at,
        storiesHeard: row.storiesHeard,
        themes: row.themes,
        completed: row.completed,
        eventId: typeof row.eventId === 'string' ? row.eventId : '',
      }));
  } catch {
    return [];
  }
}

export const history = signal<HistoryRecord[]>(load());

effect(() => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history.value)); } catch { /* private mode */ }
});

void hydrateHistory();

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 128 random bits, URL-safe. The id names the sit, not an upload attempt. */
export function randomEventId(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(16)));
}

function factsKey(row: PlayRecord): string {
  return JSON.stringify([
    row.recordingId,
    row.at,
    [...row.storiesHeard].sort(),
    [...row.themes].sort(),
  ]);
}

/**
 * SHA-256 of immutable facts plus the occurrence ordinal among otherwise identical
 * *legacy* rows. `completed` is omitted because `finishPlay` changes it after append;
 * including it would mint a second id on the same sit.
 */
export async function deriveLegacyEventId(row: PlayRecord, ordinal: number): Promise<string> {
  const material = JSON.stringify([
    row.recordingId,
    row.at,
    [...row.storiesHeard].sort(),
    [...row.themes].sort(),
    ordinal,
  ]);
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material)),
  );
  return base64Url(digest);
}

export async function withEventIds(
  rows: readonly (PlayRecord & { eventId?: string })[],
): Promise<HistoryRecord[]> {
  const counts = new Map<string, number>();
  const out: HistoryRecord[] = [];
  for (const row of rows) {
    if (typeof row.eventId === 'string' && EVENT_ID_RE.test(row.eventId)) {
      out.push({
        recordingId: row.recordingId,
        at: row.at,
        storiesHeard: row.storiesHeard,
        themes: row.themes,
        completed: row.completed,
        eventId: row.eventId,
      });
      continue;
    }
    const key = factsKey(row);
    const ordinal = counts.get(key) ?? 0;
    counts.set(key, ordinal + 1);
    out.push({
      recordingId: row.recordingId,
      at: row.at,
      storiesHeard: row.storiesHeard,
      themes: row.themes,
      completed: row.completed,
      eventId: await deriveLegacyEventId(row, ordinal),
    });
  }
  return out;
}

export async function hydrateHistory(): Promise<void> {
  try {
    const current = history.value;
    if (current.every((row) => EVENT_ID_RE.test(row.eventId))) return;
    const next = await withEventIds(current);
    // withEventIds awaits SHA-256. A sit recorded in that gap must not be dropped
    // by writing the stale snapshot back — same class of bug as editing segment.py
    // while a scan reads it.
    if (history.value !== current) {
      await hydrateHistory();
      return;
    }
    const changed = next.length !== current.length
      || next.some((row, i) => row.eventId !== current[i]?.eventId);
    if (changed) history.value = next;
  } catch {
    /* Missing Web Crypto or a private-mode write; ids wait until we can persist them. */
  }
}

function readClearedBefore(): number {
  try {
    const raw = localStorage.getItem(HISTORY_CLEAR_KEY);
    if (!raw) return 0;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeClearedBefore(value: number): void {
  try { localStorage.setItem(HISTORY_CLEAR_KEY, String(value)); } catch { /* private mode */ }
}

export function historyClearedBefore(): number {
  return readClearedBefore();
}

export function setHistoryClearedBefore(value: number): void {
  const current = readClearedBefore();
  if (value > current) writeClearedBefore(value);
}

function notifySitEnded(): void {
  void import('../account/sync').then((mod) => {
    void mod.syncHistory();
  }).catch(() => {
    /* Account screen owns the failure; a sit must not see it. */
  });
}

export function recordPlay(entry: PlayRecord): void {
  const existing = (entry as HistoryRecord).eventId;
  const eventId = typeof existing === 'string' && EVENT_ID_RE.test(existing)
    ? existing
    : randomEventId();
  history.value = [...history.value, { ...entry, eventId }];
}

/**
 * Mark the sit that just ended as completed or not.
 *
 * "Completed" is generous on purpose: someone who sits for 38 of 40 minutes and then
 * reaches for their phone has finished their meditation, whatever the progress bar says.
 */
export function finishPlay(recordingId: string, playedSec: number, plannedSec: number): void {
  const completed = playedSec >= plannedSec * 0.9;
  const list = [...history.value];
  for (let i = list.length - 1; i >= 0; i--) {
    if (list[i].recordingId === recordingId) {
      list[i] = { ...list[i], completed };
      break;
    }
  }
  history.value = list;
  notifySitEnded();
}

export function clearHistory(): void {
  setHistoryClearedBefore(Date.now());
  history.value = [];
  notifySitEnded();
}

export function lastHeard(recordingId: string): number | null {
  const hits = history.value.filter((h) => h.recordingId === recordingId);
  return hits.length ? Math.max(...hits.map((h) => h.at)) : null;
}
